import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Play, Vote, DollarSign, TrendingUp, Users, BarChart3, ArrowRight, Sparkles, Zap, Star } from "lucide-react";
import { SEO } from "@/components/SEO";
import { SectionProgressBar } from "@/components/SectionProgressBar";
import { ScrollToTopButton } from "@/components/ScrollToTopButton";
import { LazySection } from "@/components/LazySection";
import { OptimizedImage } from "@/components/OptimizedImage";
import {
  LiveStreamPreviewWithSuspense,
  ScrollEngagementPromptWithSuspense,
  ExitIntentModalWithSuspense,
  OnboardingTourWithSuspense,
} from "./Index.lazy";
import { analytics } from "@/lib/analytics";
import { calculateSizes } from "@/utils/imageOptimization";
import logo from "@/assets/logo.png";

const Index = () => {
  useEffect(() => {
    analytics.pageView('/');
    const startTime = Date.now();

    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'largest-contentful-paint') {
            analytics.userEngagement('lcp_measurement', { value: entry.startTime });
          }
        }
      });
      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    }

    return () => {
      analytics.timeOnPage(Date.now() - startTime);
    };
  }, []);

  const handleCtaClick = (ctaName: string, location: string) => {
    analytics.ctaClick(ctaName, location);
  };

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "name": "HiStage",
        "url": "https://histage.lovable.app",
        "logo": "https://histage.lovable.app/logo.png",
        "description": "Fan-driven live streaming platform connecting creators and audiences through interactive voting, tipping, and real-time engagement",
        "sameAs": ["https://twitter.com/HiStage", "https://facebook.com/HiStage", "https://instagram.com/HiStage"]
      },
      {
        "@type": "WebSite",
        "name": "HiStage",
        "url": "https://histage.lovable.app",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://histage.lovable.app/discover?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "WebApplication",
        "name": "HiStage",
        "operatingSystem": "Any",
        "applicationCategory": "EntertainmentApplication",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
      }
    ]
  };

  const features = [
    { icon: Vote, title: "Fan Voting", description: "Choose what happens next — your vote shapes the show in real time.", color: "from-primary to-primary-glow" },
    { icon: DollarSign, title: "Smart Tipping", description: "Tip to unlock backstage access, unreleased tracks, and exclusive moments.", color: "from-accent to-orange-400" },
    { icon: TrendingUp, title: "Leaderboards", description: "Rise through the ranks and earn recognition as a top supporter.", color: "from-emerald-500 to-teal-400" },
    { icon: Users, title: "Live Engagement", description: "Chat, react, and connect with creators and fans in real time.", color: "from-blue-500 to-cyan-400" },
    { icon: BarChart3, title: "Creator Analytics", description: "Track earnings, engagement, and audience growth with live dashboards.", color: "from-rose-500 to-pink-400" },
    { icon: Play, title: "HD Streaming", description: "Crystal-clear video with adaptive quality for every device and connection.", color: "from-violet-500 to-purple-400" },
  ];

  const stats = [
    { value: "10K+", label: "Active Creators" },
    { value: "2M+", label: "Fans Engaged" },
    { value: "$5M+", label: "Tips Sent" },
    { value: "99.9%", label: "Uptime" },
  ];

  return (
    <>
      <SectionProgressBar />
      <ExitIntentModalWithSuspense />
      <OnboardingTourWithSuspense />
      <ScrollEngagementPromptWithSuspense />
      <ScrollToTopButton />
      <SEO
        title="HiStage - Fan-Driven Live Streaming Platform"
        description="Watch live performances and engage with creators through interactive voting, tipping, and real-time chat. Join thousands of fans and creators on HiStage."
        image="/assets/hero-stage.jpg"
        url="/"
        keywords={["live streaming platform", "fan engagement", "creator platform", "interactive live shows", "live tipping", "fan voting", "live performances"]}
        schema={schema}
      />

      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <nav className="glass-surface sticky top-0 z-50 border-b" role="navigation" aria-label="Main navigation">
          <div className="container mx-auto px-4 sm:px-6 py-3">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center shrink-0">
                <img src={logo} alt="HiStage Logo" className="h-9 sm:h-11 md:h-14 w-auto brightness-110" width="200" height="64" />
              </Link>
              <div className="hidden md:flex items-center gap-6 lg:gap-8">
                <a href="#features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Features</a>
                <a href="#how-it-works" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
                <Link to="/blog" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Blog</Link>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <Link to="/auth">
                  <Button variant="ghost" size="sm" className="text-sm font-medium hidden sm:inline-flex">Log In</Button>
                </Link>
                <Link to="/auth" onClick={() => handleCtaClick('Sign Up', 'nav')}>
                  <Button size="sm" className="rounded-full px-4 sm:px-5 text-xs sm:text-sm font-medium shadow-md hover:shadow-lg transition-shadow">
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        <main>
          {/* Hero + Stats Bento */}
          <section className="relative py-8 sm:py-12 md:py-16 lg:py-20 overflow-hidden" aria-labelledby="hero-heading">
            {/* Vapor backdrop orbs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
              <div className="absolute -top-24 -right-24 sm:-top-40 sm:-right-40 w-72 sm:w-[520px] h-72 sm:h-[520px] rounded-full bg-[hsl(var(--cyan-300))]/40 blur-3xl" />
              <div className="absolute -bottom-24 -left-24 sm:-bottom-40 sm:-left-40 w-72 sm:w-[480px] h-72 sm:h-[480px] rounded-full bg-[hsl(var(--lavender-400))]/40 blur-3xl" />
            </div>

            <div className="container mx-auto px-4 sm:px-6 relative">
              <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-min gap-3 sm:gap-4 md:gap-5">
                {/* Hero Block — spans 8 cols on desktop */}
                <article className="md:col-span-12 lg:col-span-8 bg-card border border-[hsl(var(--lavender-400))]/40 rounded-3xl p-6 sm:p-8 md:p-10 lg:p-12 relative overflow-hidden shadow-sm">
                  <div className="absolute -top-24 -left-24 w-64 h-64 rounded-full bg-[hsl(var(--cyan-300))]/50 blur-[100px]" aria-hidden="true" />
                  <div className="absolute -bottom-24 -right-24 w-64 h-64 rounded-full bg-[hsl(var(--lavender-400))]/50 blur-[100px]" aria-hidden="true" />

                  <div className="relative z-10 flex flex-col justify-center">
                    <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full bg-[hsl(var(--lavender-400))]/15 border border-[hsl(var(--lavender-400))]/30 text-[hsl(var(--indigo-600))] text-xs sm:text-sm font-medium mb-6 sm:mb-8 w-fit">
                      <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                      Now in open beta
                    </div>

                    <h1 id="hero-heading" className="text-foreground mb-5 sm:mb-6">
                      The <span className="gradient-text">Fan-Driven</span><br className="hidden sm:block" /> Live Stage
                    </h1>

                    <p className="text-muted-foreground max-w-lg text-base sm:text-lg lg:text-xl leading-relaxed mb-7 sm:mb-9">
                      Watch your favorite artists perform live. Vote on what happens next. Tip to unlock exclusive moments.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link to="/auth" onClick={() => handleCtaClick('Get Started Free', 'hero')} className="w-full sm:w-auto">
                        <Button size="lg" className="rounded-2xl px-7 sm:px-8 text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl transition-all group w-full touch-target">
                          Get Started Free
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                      <a href="#features" className="w-full sm:w-auto">
                        <Button size="lg" variant="outline" className="rounded-2xl px-7 sm:px-8 text-sm sm:text-base font-semibold w-full touch-target">
                          See How It Works
                        </Button>
                      </a>
                    </div>

                    {/* Social proof */}
                    <div className="flex items-center gap-3 pt-6 sm:pt-7">
                      <div className="flex -space-x-2">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="h-7 w-7 sm:h-8 sm:w-8 rounded-full border-2 border-card bg-gradient-to-br from-[hsl(var(--indigo-500))] to-[hsl(var(--cyan-400))]" />
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Star key={i} className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-[hsl(var(--cyan-500))] fill-[hsl(var(--cyan-400))]" />
                          ))}
                        </div>
                        <span className="text-xs sm:text-sm text-muted-foreground font-medium">Loved by 10K+ fans</span>
                      </div>
                    </div>
                  </div>
                </article>

                {/* Live Preview Block — spans 4 cols on desktop, full on mobile/tablet */}
                <div className="md:col-span-12 lg:col-span-4 bg-[hsl(var(--slate-900))] rounded-3xl overflow-hidden relative group min-h-[280px] sm:min-h-[340px] lg:min-h-full">
                  <OptimizedImage
                    src="/assets/hero-stage.jpg"
                    webpSrc="/assets/hero-stage.webp"
                    avifSrc="/assets/hero-stage.avif"
                    srcSet="/assets/hero-stage-640w.jpg 640w, /assets/hero-stage-800w.jpg 800w, /assets/hero-stage-1200w.jpg 1200w, /assets/hero-stage.jpg 1920w"
                    webpSrcSet="/assets/hero-stage-640w.webp 640w, /assets/hero-stage-800w.webp 800w, /assets/hero-stage-1200w.webp 1200w, /assets/hero-stage.webp 1920w"
                    avifSrcSet="/assets/hero-stage-640w.avif 640w, /assets/hero-stage-800w.avif 800w, /assets/hero-stage-1200w.avif 1200w, /assets/hero-stage.avif 1920w"
                    sizes={calculateSizes({ mobile: '100vw', tablet: '100vw', default: '33vw' })}
                    alt="Musician performing live on HiStage with interactive fan engagement"
                    className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-60 transition-opacity duration-700"
                    priority={true}
                    width={1200}
                    height={900}
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--indigo-500))]/30 via-transparent to-[hsl(var(--cyan-400))]/20" aria-hidden="true" />

                  {/* Top badges */}
                  <div className="absolute top-4 left-4 sm:top-5 sm:left-5 flex gap-2 z-10">
                    <span className="bg-destructive text-destructive-foreground text-[10px] font-bold px-2 py-1 rounded-md flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-destructive-foreground animate-pulse" />
                      LIVE
                    </span>
                    <span className="bg-white/15 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded-md border border-white/20">
                      2.4K watching
                    </span>
                  </div>

                  {/* Center play button */}
                  <div className="relative z-10 h-full min-h-[280px] sm:min-h-[340px] lg:min-h-[460px] flex items-center justify-center">
                    <button
                      className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-[hsl(var(--cyan-400))] flex items-center justify-center shadow-2xl shadow-[hsl(var(--cyan-400))]/50 hover:scale-110 transition-transform cursor-pointer touch-target animate-pulse-glow"
                      aria-label="Play video demo"
                    >
                      <Play className="h-7 w-7 sm:h-9 sm:w-9 text-[hsl(var(--slate-900))] fill-[hsl(var(--slate-900))] ml-0.5" aria-hidden="true" />
                    </button>
                  </div>

                  {/* Audio-bars deco */}
                  <div className="absolute bottom-5 left-5 right-5 z-10">
                    <div className="flex items-end gap-1 h-10 sm:h-12">
                      {[50, 75, 100, 65, 50, 80, 60, 90, 70, 55].map((h, i) => (
                        <div
                          key={i}
                          className={`flex-1 rounded-t-sm ${i % 3 === 0 ? 'bg-[hsl(var(--cyan-400))]/70' : i % 3 === 1 ? 'bg-[hsl(var(--indigo-500))]/70' : 'bg-[hsl(var(--lavender-400))]/70'}`}
                          style={{ height: `${h}%` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Stats bento row */}
                <div className="md:col-span-6 lg:col-span-3 bg-card border border-border rounded-2xl p-5 sm:p-6 md:p-7 flex flex-col justify-between hover:border-[hsl(var(--lavender-400))] transition-colors min-h-[120px] sm:min-h-[140px]">
                  <div className="text-[hsl(var(--indigo-500))] font-medium text-xs sm:text-sm">Creators</div>
                  <div>
                    <div className="font-display text-3xl sm:text-4xl font-bold text-foreground">10K+</div>
                    <div className="text-xs sm:text-sm text-muted-foreground mt-1">Active globally</div>
                  </div>
                </div>

                <div className="md:col-span-6 lg:col-span-3 bg-card border border-border rounded-2xl p-5 sm:p-6 md:p-7 flex flex-col justify-between hover:border-[hsl(var(--cyan-400))] transition-colors min-h-[120px] sm:min-h-[140px]">
                  <div className="text-[hsl(var(--cyan-500))] font-medium text-xs sm:text-sm">Engaged</div>
                  <div>
                    <div className="font-display text-3xl sm:text-4xl font-bold text-foreground">2M+</div>
                    <div className="text-xs sm:text-sm text-muted-foreground mt-1">Fans per month</div>
                  </div>
                </div>

                <div className="md:col-span-6 lg:col-span-3 bg-[hsl(var(--cyan-300))]/30 border border-[hsl(var(--cyan-300))]/60 rounded-2xl p-5 sm:p-6 md:p-7 flex flex-col justify-between min-h-[120px] sm:min-h-[140px]">
                  <div className="text-foreground font-medium text-xs sm:text-sm">Tips Sent</div>
                  <div>
                    <div className="font-display text-3xl sm:text-4xl font-bold text-foreground">$5M+</div>
                    <div className="text-xs sm:text-sm text-foreground mt-1">Community earned</div>
                  </div>
                </div>

                <div className="md:col-span-6 lg:col-span-3 bg-[hsl(var(--indigo-500))] rounded-2xl p-5 sm:p-6 md:p-7 flex flex-col justify-between text-primary-foreground min-h-[120px] sm:min-h-[140px]">
                  <div className="text-primary-foreground font-medium text-xs sm:text-sm">Uptime</div>
                  <div>
                    <div className="font-display text-3xl sm:text-4xl font-bold">99.9%</div>
                    <div className="text-xs sm:text-sm text-primary-foreground mt-1">Reliable streaming</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <LazySection rootMargin="200px">
            <LiveStreamPreviewWithSuspense />
          </LazySection>

          {/* Interactive Demo Section */}
          <LazySection rootMargin="100px">
            <section className="py-12 sm:py-16 md:py-24 lg:py-28" id="how-it-works" aria-labelledby="discover-heading">
              <div className="container mx-auto px-4 sm:px-6">
                <div className="text-center mb-8 sm:mb-12 md:mb-16">
                  <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full border border-primary/20 bg-primary/[0.06] text-primary text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                    <Zap className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                    See it in action
                  </div>
                  <h2 id="discover-heading" className="text-foreground mb-3 sm:mb-4">Your Stage, Your Rules</h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base lg:text-lg px-2">
                    From voting on setlists to tipping for encore performances — every interaction matters.
                  </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-5 sm:gap-6 lg:gap-8 items-start">
                  {/* Vote Section */}
                  <article className="elevated-card rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-5 lg:space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Vote className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                      </div>
                      <h3 className="font-bold text-foreground text-sm sm:text-base">Vote for the Next Song</h3>
                    </div>
                    <div className="space-y-2" role="radiogroup" aria-label="Song selection">
                      {["Midnight Dreams", "Electric Sunrise", "Neon Heartbeat"].map((song, i) => (
                        <label key={song} className="flex items-center gap-3 p-3 sm:p-3.5 rounded-lg sm:rounded-xl bg-muted/40 hover:bg-muted/70 cursor-pointer transition-all border border-transparent hover:border-primary/20 group touch-target">
                          <div className="h-4 w-4 sm:h-5 sm:w-5 rounded-full border-2 border-muted-foreground/30 group-hover:border-primary flex items-center justify-center transition-colors shrink-0">
                            <div className="h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-primary opacity-0 group-hover:opacity-50 transition-opacity" />
                          </div>
                          <span className="text-sm text-foreground font-medium truncate">{song}</span>
                          <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">{[342, 287, 195][i]} votes</span>
                        </label>
                      ))}
                    </div>
                    <Button className="w-full rounded-full py-5 text-sm sm:text-base font-medium shadow-md touch-target">
                      Cast Your Vote
                    </Button>

                    {/* Tipping */}
                    <div className="pt-4 sm:pt-5 border-t space-y-3 sm:space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl bg-accent/15 flex items-center justify-center shrink-0">
                          <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-accent" />
                        </div>
                        <h3 className="font-bold text-foreground text-sm sm:text-base">Tipping & Unlocks</h3>
                      </div>
                      <div className="space-y-3">
                        <label htmlFor="tip-amount" className="flex items-center justify-between">
                          <span className="text-xs sm:text-sm text-muted-foreground">Tip amount</span>
                          <span className="text-xl sm:text-2xl font-bold font-display text-foreground">100 <span className="text-xs sm:text-sm font-normal text-muted-foreground">StagCos</span></span>
                        </label>
                        <input
                          id="tip-amount"
                          type="range"
                          min="0"
                          max="1000"
                          defaultValue="100"
                          aria-label="Tip amount in StagCoins"
                          className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-background"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { name: "Backstage Q&A", cost: 500 },
                            { name: "Unreleased Songs", cost: 1000 },
                          ].map((item) => (
                            <div key={item.name} className="p-2.5 sm:p-3 rounded-lg sm:rounded-xl bg-muted/40 border border-border/50 text-center">
                              <p className="text-[11px] sm:text-xs text-muted-foreground truncate">{item.name}</p>
                              <p className="text-xs sm:text-sm font-bold text-foreground mt-0.5">{item.cost} StagCos</p>
                            </div>
                          ))}
                        </div>
                        <Button className="w-full rounded-full py-5 text-sm sm:text-base font-medium touch-target">
                          Send Tip 🎉
                        </Button>
                      </div>
                    </div>
                  </article>

                  {/* Live stream + Dashboard */}
                  <div className="space-y-5 sm:space-y-6">
                    <div className="elevated-card rounded-xl sm:rounded-2xl lg:rounded-3xl overflow-hidden">
                      <div className="relative">
                        <OptimizedImage
                          src="/assets/hero-stage-small.jpg"
                          webpSrc="/assets/hero-stage-small.webp"
                          avifSrc="/assets/hero-stage-small.avif"
                          srcSet="/assets/hero-stage-small.jpg 800w"
                          webpSrcSet="/assets/hero-stage-small.webp 800w"
                          avifSrcSet="/assets/hero-stage-small.avif 800w"
                          sizes={calculateSizes({ mobile: '100vw', tablet: '50vw', default: '50vw' })}
                          alt="Live streaming interface"
                          className="w-full aspect-video object-cover"
                          width={800}
                          height={450}
                          aspectRatio="16/9"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-foreground/50 to-transparent" />
                        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex items-center gap-1.5">
                          <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-destructive text-destructive-foreground text-[10px] sm:text-xs font-bold rounded-md flex items-center gap-1">
                            <span className="h-1 w-1 sm:h-1.5 sm:w-1.5 rounded-full bg-destructive-foreground animate-pulse" />
                            LIVE
                          </span>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <button className="h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-background/90 flex items-center justify-center shadow-lg touch-target" aria-label="Play live stream">
                            <Play className="h-5 w-5 sm:h-6 sm:w-6 text-primary fill-primary ml-0.5" aria-hidden="true" />
                          </button>
                        </div>
                        {/* Bottom stats overlay */}
                        <div className="absolute bottom-2 left-2 right-2 sm:bottom-3 sm:left-3 sm:right-3 flex items-center justify-between">
                          <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-foreground/50 backdrop-blur text-background text-[10px] sm:text-xs font-medium rounded-md">🎵 DJ Nova</span>
                          <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-foreground/50 backdrop-blur text-background text-[10px] sm:text-xs font-medium rounded-md">👁 1,247</span>
                        </div>
                      </div>
                    </div>

                    {/* Creator Dashboard Preview */}
                    <div className="elevated-card rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 md:p-7 space-y-4 sm:space-y-5">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-foreground text-sm sm:text-base">Creator Dashboard</h3>
                        <span className="text-[10px] sm:text-xs text-primary font-medium px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-primary/10">Live</span>
                      </div>

                      <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4">
                        {[
                          { label: "Viewers", value: "1,247", change: "+12%" },
                          { label: "Tips", value: "$345", change: "+28%" },
                          { label: "Votes", value: "824", change: "+8%" },
                        ].map((metric) => (
                          <div key={metric.label} className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-muted/40 text-center">
                            <p className="text-[10px] sm:text-xs text-muted-foreground">{metric.label}</p>
                            <p className="text-base sm:text-lg md:text-xl font-bold font-display text-foreground">{metric.value}</p>
                            <p className="text-[10px] sm:text-xs text-emerald-600 font-medium">{metric.change}</p>
                          </div>
                        ))}
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between text-[10px] sm:text-xs text-muted-foreground">
                          <span>Engagement over time</span>
                          <span>Last 60 min</span>
                        </div>
                        <div className="h-16 sm:h-20 flex items-end gap-0.5 sm:gap-1">
                          {[35, 50, 40, 65, 55, 80, 60, 90, 75, 85, 70, 95].map((height, i) => (
                            <div key={i} className="flex-1 rounded-t-sm bg-primary/15 relative" style={{ height: `${height}%` }}>
                              <div className="absolute inset-0 rounded-t-sm bg-gradient-to-t from-primary to-primary-glow" style={{ height: '65%', bottom: 0, position: 'absolute' }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </LazySection>

          {/* Features Grid */}
          <LazySection rootMargin="150px">
            <section className="py-12 sm:py-16 md:py-24 lg:py-28 bg-muted/20" id="features" aria-labelledby="features-heading">
              <div className="container mx-auto px-4 sm:px-6">
                <div className="text-center mb-8 sm:mb-12 md:mb-16">
                  <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full border border-primary/20 bg-primary/[0.06] text-primary text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                    <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                    Platform features
                  </div>
                  <h2 id="features-heading" className="text-foreground mb-3 sm:mb-4">
                    Everything You Need to Shine
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base lg:text-lg px-2">
                    Built for creators who want to connect deeply with their audience, and fans who want more than just watching.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
                  {features.map((feature, i) => (
                    <div
                      key={i}
                      className="elevated-card rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 lg:p-7 group cursor-pointer"
                      role="article"
                      aria-labelledby={`feature-${i}-title`}
                    >
                      <div className={`h-10 w-10 sm:h-11 sm:w-11 rounded-lg sm:rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-3 sm:mb-4 shadow-sm group-hover:scale-110 transition-transform`} aria-hidden="true">
                        <feature.icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                      </div>
                      <h3 id={`feature-${i}-title`} className="font-bold text-foreground mb-1.5 sm:mb-2 text-sm sm:text-base">{feature.title}</h3>
                      <p className="text-muted-foreground leading-relaxed text-xs sm:text-sm">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </LazySection>

          {/* CTA Section */}
          <LazySection rootMargin="100px">
            <section className="py-12 sm:py-16 md:py-24 lg:py-28" aria-labelledby="cta-heading">
              <div className="container mx-auto px-4 sm:px-6">
                <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden gradient-purple p-6 sm:p-10 md:p-14 lg:p-20 text-center max-w-4xl mx-auto">
                  {/* Decorative orbs */}
                  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
                    <div className="absolute -top-10 -right-10 sm:-top-20 sm:-right-20 w-32 sm:w-60 h-32 sm:h-60 rounded-full bg-primary-foreground/10 blur-2xl" />
                    <div className="absolute -bottom-10 -left-10 sm:-bottom-20 sm:-left-20 w-24 sm:w-48 h-24 sm:h-48 rounded-full bg-primary-foreground/5 blur-2xl" />
                  </div>
                  <div className="relative z-10">
                    <h2 id="cta-heading" className="text-primary-foreground mb-3 sm:mb-5 md:mb-6">
                      Ready to Take the Stage?
                    </h2>
                    <p className="text-primary-foreground mb-6 sm:mb-8 md:mb-10 max-w-xl mx-auto text-sm sm:text-base lg:text-lg px-2">
                      Join thousands of creators and fans already making unforgettable moments on HiStage.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Link to="/auth" onClick={() => handleCtaClick('Start Creating', 'cta-section')} className="w-full sm:w-auto">
                        <Button size="lg" variant="secondary" className="rounded-full px-6 sm:px-8 md:px-10 text-sm sm:text-base font-medium shadow-lg w-full touch-target">
                          Start Creating — It's Free
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                      <Link to="/auth" onClick={() => handleCtaClick('Join as Fan', 'cta-section')} className="w-full sm:w-auto">
                        <Button size="lg" variant="outline" className="rounded-full px-6 sm:px-8 md:px-10 text-sm sm:text-base font-medium border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 w-full touch-target">
                          Join as a Fan
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </LazySection>
        </main>

        {/* Footer */}
        <LazySection rootMargin="50px">
          <footer className="border-t py-10 sm:py-12 md:py-16 bg-muted/20" role="contentinfo" aria-label="Footer">
            <div className="container mx-auto px-4 sm:px-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10 mb-8 sm:mb-10 md:mb-12">
                <div className="sm:col-span-2 md:col-span-1">
                  <div className="mb-3 sm:mb-4">
                    <img src={logo} alt="HiStage Logo" className="h-9 sm:h-10 md:h-12 w-auto brightness-110" width="150" height="48" />
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    Fan-powered. Creator-owned.<br />The stage belongs to everyone.
                  </p>
                </div>
                <div>
                  <h3 className="text-xs sm:text-sm font-semibold text-foreground mb-2.5 sm:mb-3 uppercase tracking-wider">Product</h3>
                  <div className="space-y-2 sm:space-y-2.5 text-xs sm:text-sm text-muted-foreground">
                    <a href="#features" className="block hover:text-foreground transition-colors py-0.5">Features</a>
                    <div className="hover:text-foreground transition-colors cursor-pointer py-0.5">Pricing</div>
                    <div className="hover:text-foreground transition-colors cursor-pointer py-0.5">For Creators</div>
                  </div>
                </div>
                <div>
                  <h3 className="text-xs sm:text-sm font-semibold text-foreground mb-2.5 sm:mb-3 uppercase tracking-wider">Company</h3>
                  <div className="space-y-2 sm:space-y-2.5 text-xs sm:text-sm text-muted-foreground">
                    <Link to="/about" className="block hover:text-foreground transition-colors py-0.5">About</Link>
                    <Link to="/blog" className="block hover:text-foreground transition-colors py-0.5">Blog</Link>
                    <Link to="/careers" className="block hover:text-foreground transition-colors py-0.5">Careers</Link>
                  </div>
                </div>
                <div>
                  <h3 className="text-xs sm:text-sm font-semibold text-foreground mb-2.5 sm:mb-3 uppercase tracking-wider">Support</h3>
                  <div className="space-y-2 sm:space-y-2.5 text-xs sm:text-sm text-muted-foreground">
                    <Link to="/help" className="block hover:text-foreground transition-colors py-0.5">Help Center</Link>
                    <Link to="/contact" className="block hover:text-foreground transition-colors py-0.5">Contact</Link>
                    <Link to="/terms" className="block hover:text-foreground transition-colors py-0.5">Terms</Link>
                    <Link to="/privacy" className="block hover:text-foreground transition-colors py-0.5">Privacy</Link>
                    <Link to="/csae-policy" className="block hover:text-foreground transition-colors py-0.5">Child Safety</Link>
                  </div>
                </div>
              </div>
              <div className="pt-6 sm:pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
                <span>© 2025 HiStage. All rights reserved.</span>
                <div className="flex items-center gap-5 sm:gap-6">
                  <a href="https://twitter.com/HiStage" className="hover:text-foreground transition-colors py-1">Twitter</a>
                  <a href="https://instagram.com/HiStage" className="hover:text-foreground transition-colors py-1">Instagram</a>
                  <a href="https://discord.gg/HiStage" className="hover:text-foreground transition-colors py-1">Discord</a>
                </div>
              </div>
            </div>
          </footer>
        </LazySection>
      </div>
    </>
  );
};

export default Index;
